import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "ugc-full-media__info" }
const _hoisted_2 = {
  key: 0,
  class: "ugc-full-media__name paragraph-regular"
}
const _hoisted_3 = ["src", "alt"]

import {
  computed, inject, nextTick, onBeforeUnmount, onMounted, ref,
} from 'vue';
import { toLitiumMediaUrlKey } from '@distancify/drapejs-litium/lib/keys';
import { useLazyLoad } from '@/composables/useLazyLoad';
import ProductTag from './ProductTag.vue';
import { bracketImageRectOnWidth } from '@/helpers/image-size-calc';


export default /*#__PURE__*/_defineComponent({
  __name: 'UGCFullMedia',
  props: {
  products: {
    type: Array,
  },
  fileName: {
    type: String,
  },
  imageId: {
    type: String,
    required: true,
  },
  imageAlt: {
    type: String,
    default: null,
  },
  focalTop: {
    type: Number,
  },
  focalLeft: {
    type: Number,
  },
  username: {
    type: String,
  },
  caption: {
    type: String,
  },
},
  setup(__props) {

const props = __props

const imageRectBracketStep = 50;

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
const toLitiumMediaUrl = inject(toLitiumMediaUrlKey, (id, opts) => '');

// eslint-disable-next-line no-undef


const root = ref<null | HTMLElement>(null);
const image = ref<null | HTMLImageElement>(null);
const imageNaturalWidth = ref<null | number>(null);
const imageNaturalHeight = ref<null | number>(null);
const mediaDisplayWidth = ref<null | number>(null);
const mediaDisplayHeight = ref<null | number>(null);
const isToggleAll = ref(false);
const mediaDisplay = ref<null | HTMLElement>(null);

const keyedProducts = computed(() => props.products?.map((p: any) => ({
  ...p,
  key: Math.random().toString(36).slice(2),
})) || []);

const expandedTag = ref<null | string>(null);

const usableProducts = computed(() => keyedProducts.value.map((p: any) => ({
  ...p,
  isExpanded: expandedTag.value === p.key,
  positionX: getTagX(p),
  positionY: getTagY(p),
}))
  .filter((p) => p.positionX <= 100
    && p.positionX >= 0
    && p.positionY <= 100
    && p.positionY >= 0));

const imageAspectRatio = computed(() => {
  if (!imageNaturalWidth.value || !imageNaturalHeight.value) return 0;

  return imageNaturalWidth.value / imageNaturalHeight.value;
});

const mediaDisplayAspectRatio = computed(() => {
  if (!mediaDisplayWidth.value || !mediaDisplayHeight.value) return 0;
  return mediaDisplayWidth.value / mediaDisplayHeight.value;
});

const imageUrl = computed(() => toLitiumMediaUrl(
  props.imageId,
  { maxWidth: 200, skipPixelRatio: true },
));

const userHandle = computed(() => {
  return props.username ? `@${props.username}` : '';
});

const imageUrlCropped = computed(() => {
  if (!mediaDisplayWidth.value) return '';

  return toLitiumMediaUrl(props.imageId, {
    fillArea: {
      ...bracketImageRectOnWidth({ width: mediaDisplayWidth.value, height: mediaDisplayHeight.value }, imageRectBracketStep),
      focalPoint: focalPoint.value,
    },
  });
});

const focalPoint = computed(() => ({
  top: props.focalTop || 50,
  left: props.focalLeft || 50,
}));

const isImageLoadedAndVisible = computed(
  () => typeof mediaDisplayWidth.value === 'number'
    && typeof mediaDisplayHeight.value === 'number'
    && typeof imageNaturalWidth.value === 'number'
    && typeof imageNaturalHeight.value === 'number',
);

function getTagX(tag) {
  const croppedOnWidth = mediaDisplayAspectRatio.value < imageAspectRatio.value;
  if (!croppedOnWidth) return tag.position.left;
  const uncroppedAspectCorrectedValue = mediaDisplayHeight.value! * imageAspectRatio.value;
  return calculateAdjustedTagPosition(tag.position.left, mediaDisplayWidth.value, props.focalLeft, uncroppedAspectCorrectedValue);
}

function getTagY(tag) {
  const croppedOnHeight = mediaDisplayAspectRatio.value > imageAspectRatio.value;
  if (!croppedOnHeight) return tag.position.top;

  const uncroppedAspectCorrectedValue = mediaDisplayWidth.value! / imageAspectRatio.value;
  return calculateAdjustedTagPosition(tag.position.top, mediaDisplayHeight.value, props.focalTop, uncroppedAspectCorrectedValue);
}

function loadImage() {
  imageNaturalWidth.value = image.value!.naturalWidth;
  imageNaturalHeight.value = image.value!.naturalHeight;

  image.value!.removeEventListener('load', loadImage);
  image.value = null;
}

function calculateAdjustedTagPosition(
  tagPositionPercentage,
  dimensionPx,
  imageCropPercentage,
  uncroppedDimensionPx,
) {
  const croppedPx = uncroppedDimensionPx - dimensionPx;
  const imageCropFactor = imageCropPercentage / 100;
  const relevantCroppedPx = croppedPx * imageCropFactor;
  const tagPositionFactor = tagPositionPercentage / 100;
  const uncroppedTagPosPx = uncroppedDimensionPx * tagPositionFactor;
  const adjustedPosition = uncroppedTagPosPx - relevantCroppedPx;
  const adjustedPositionFactor = constrainFrom0To1(adjustedPosition / dimensionPx);

  return adjustedPositionFactor * 100;

  function constrainFrom0To1(val) {
    return Math.min(Math.max(val, 0), 1);
  }
}

function recalculateSizes() {
  if (!mediaDisplay.value) {
    console.warn('media display not set');
    return;
  }
  const bounds = mediaDisplay.value.getBoundingClientRect();
  mediaDisplayWidth.value = Math.round(bounds.width);
  mediaDisplayHeight.value = Math.round(bounds.height);
}

function expandTag(tag) {
  expandedTag.value = tag.key;
}

useLazyLoad(root, () => {
  image.value = new Image();
  image.value.addEventListener('load', loadImage);
  image.value.src = imageUrl.value;

  nextTick(() => {
    recalculateSizes();
  });
});

onMounted(() => {
  mediaDisplay.value?.focus();
  window.addEventListener('resize', recalculateSizes);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', recalculateSizes);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "ugc-full-media",
    ref_key: "root",
    ref: root
  }, [
    _createElementVNode("div", _hoisted_1, [
      (_unref(userHandle))
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(_unref(userHandle)), 1))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", {
      tabindex: "0",
      class: "ugc-full-media__image-container",
      ref_key: "mediaDisplay",
      ref: mediaDisplay
    }, [
      (_unref(imageUrlCropped))
        ? (_openBlock(), _createElementBlock("img", {
            key: 0,
            class: "ugc-full-media__image",
            src: _unref(imageUrlCropped),
            alt: __props.imageAlt
          }, null, 8, _hoisted_3))
        : _createCommentVNode("", true),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(usableProducts), (tag) => {
        return (_openBlock(), _createBlock(ProductTag, {
          class: "ugc-full-media__tag",
          key: tag.key,
          "position-x": tag.positionX,
          "position-y": tag.positionY,
          variant: tag.variant,
          "is-image-loaded-and-visible": _unref(isImageLoadedAndVisible),
          "show-instantly": true,
          "is-expanded": isToggleAll.value || tag.isExpanded,
          onExpandTag: ($event: any) => (expandTag(tag)),
          onProductClick: ($event: any) => (_ctx.$emit('product-click', tag))
        }, null, 8, ["position-x", "position-y", "variant", "is-image-loaded-and-visible", "is-expanded", "onExpandTag", "onProductClick"]))
      }), 128))
    ], 512)
  ], 512))
}
}

})